import { API_V2 } from '../lib/axios';
import { ITag } from '@clickster/constant-definitions/dist-esm/entities/tag/types';
import { fetchAllPaginated } from './utils/fetch-all-paginated';

const TAGS_ENDPOINT = '/tags';

const getTags = async (params = {}) => {
  return fetchAllPaginated<ITag>(API_V2, TAGS_ENDPOINT, params);
};

const createTag = async (tag: ITag) => {
  const response = await API_V2.post(TAGS_ENDPOINT, tag);
  return response.data;
};

const updateTag = async (tagId: string, tag: ITag) => {
  const response = await API_V2.put(`${TAGS_ENDPOINT}/${tagId}`, tag);
  return response.data;
};

const deleteTag = async (tagId: string) => {
  console.log('[TAG_DELETE_SERVICE] Starting deleteTag API call');
  console.log('[TAG_DELETE_SERVICE] Tag ID to delete:', tagId);
  
  // Use the bulk delete endpoint with single ID (soft delete: sets deleted: true)  
  // Backend validation expects ids as array: Joi.array().items(Joi.string().alphanum().length(24))
  // Try multiple approaches to get the right query format
  
  // Approach 1: Try with proper Axios params (should work with newer Axios versions)
  console.log('[TAG_DELETE_SERVICE] Approach 1: Using proper Axios params with array');
  let response;
  try {
    response = await API_V2.delete(TAGS_ENDPOINT, {
      params: { ids: [tagId] }
    });
    console.log('[TAG_DELETE_SERVICE] Approach 1 successful');
  } catch (axiosError) {
    console.log('[TAG_DELETE_SERVICE] Approach 1 failed, trying manual URL construction');
    
    // Approach 2: Manual URL with multiple query params (standard way)
    const url = `${TAGS_ENDPOINT}?ids=${encodeURIComponent(tagId)}`;
    console.log('[TAG_DELETE_SERVICE] Approach 2 URL:', url);
    
    try {
      response = await API_V2.delete(url);
      console.log('[TAG_DELETE_SERVICE] Approach 2 successful');
    } catch (manualError) {
      console.log('[TAG_DELETE_SERVICE] Approach 2 failed, trying array notation');
      
      // Approach 3: Array notation in URL
      const arrayUrl = `${TAGS_ENDPOINT}?ids[]=${encodeURIComponent(tagId)}`;
      console.log('[TAG_DELETE_SERVICE] Approach 3 URL:', arrayUrl);
      response = await API_V2.delete(arrayUrl);
      console.log('[TAG_DELETE_SERVICE] Approach 3 successful');
    }
  }
  
  console.log('[TAG_DELETE_SERVICE] Final request URL:', response.request?.responseURL || 'URL not available');
  console.log('[TAG_DELETE_SERVICE] Delete API response status:', response.status);
  console.log('[TAG_DELETE_SERVICE] Delete API response data:', response.data);
  
  // Validate that the deletion actually worked
  const { n = 0, nModified = 0, ok = 0 } = response.data || {};
  console.log('[TAG_DELETE_SERVICE] Deletion metrics:', { n, nModified, ok });
  
  if (n === 0) {
    console.error('[TAG_DELETE_SERVICE] WARNING: No documents matched the query. Tag may not exist or query format is wrong.');
  }
  
  if (nModified === 0 && n > 0) {
    console.error('[TAG_DELETE_SERVICE] WARNING: Documents matched but none were modified. Tag may already be deleted.');
  }
  
  if (ok !== 1) {
    console.error('[TAG_DELETE_SERVICE] WARNING: Operation did not complete successfully (ok !== 1)');
  }
  
  // If bulk delete didn't work, try individual update as fallback
  if (n === 0 && nModified === 0) {
    console.log('[TAG_DELETE_SERVICE] Bulk delete failed, attempting fallback: individual update to set status=inactive');
    try {
      // Get the current tag first
      const currentTagResponse = await API_V2.get(`${TAGS_ENDPOINT}/${tagId}`);
      const currentTag = currentTagResponse.data;
      console.log('[TAG_DELETE_SERVICE] Current tag for fallback:', currentTag);
      
      // Update it to inactive status
      const updatedTag = {
        ...currentTag,
        status: 'inactive'
      };
      
      const fallbackResponse = await API_V2.put(`${TAGS_ENDPOINT}/${tagId}`, updatedTag);
      console.log('[TAG_DELETE_SERVICE] Fallback update response:', fallbackResponse.data);
      
      return { fallbackUsed: true, result: fallbackResponse.data };
      
    } catch (fallbackError) {
      console.error('[TAG_DELETE_SERVICE] Fallback approach also failed:', fallbackError);
      return response.data; // Return original response
    }
  }
  
  return response.data;
};



export const TagService = {
  getTags,
  createTag,
  updateTag,
  deleteTag
};
